// External imports
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Internal imports
import { restaurantsService, transformGooglePlacesToSaveFormat } from "../services/restaurants.service";
import { GooglePlacesRestaurant } from "../types/restaurants.types";

/**
 * Hook for saving/removing restaurants from favorites
 */
export const useSaveRestaurant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (restaurant: GooglePlacesRestaurant) => {
      const saveData = transformGooglePlacesToSaveFormat(restaurant);
      return await restaurantsService.saveRestaurant(saveData);
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch saved restaurant queries
      queryClient.invalidateQueries({ queryKey: ["saved-restaurants"] });
      queryClient.invalidateQueries({
        queryKey: ["saved-check", variables.place_id]
      });

      // Note: Save is now idempotent, so this runs even if already saved
      toast.success(`${variables.name} saved to your favorites!`);
    },
    onError: (error: Error, variables) => {
      console.error("Failed to save restaurant:", error);
      toast.error(`Failed to save ${variables.name}. Please try again.`);
    },
  });
};

/**
 * Hook for removing a restaurant from favorites
 */
export const useRemoveRestaurant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (restaurantId: string) => {
      return await restaurantsService.deleteRestaurant(restaurantId);
    },
    onSuccess: (_, restaurantId) => {
      // Invalidate saved restaurant queries
      queryClient.invalidateQueries({ queryKey: ["saved-restaurants"] });
      queryClient.invalidateQueries({ queryKey: ["saved-restaurant", restaurantId] });

      toast.success("Restaurant removed from favorites");
    },
    onError: (error: Error) => {
      console.error("Failed to remove restaurant:", error);
      toast.error("Failed to remove restaurant. Please try again.");
    },
  });
};

/**
 * Hook for toggling restaurant save status (save if not saved, remove if saved)
 */
export const useToggleRestaurant = () => {
  const saveRestaurant = useSaveRestaurant();
  const removeRestaurant = useRemoveRestaurant();

  return {
    toggleRestaurant: async (
      restaurant: GooglePlacesRestaurant,
      currentSavedRestaurantId?: string
    ) => {
      if (currentSavedRestaurantId) {
        // Restaurant is saved, remove it
        return removeRestaurant.mutateAsync(currentSavedRestaurantId);
      } else {
        // Restaurant is not saved, save it
        return saveRestaurant.mutateAsync(restaurant);
      }
    },
    isLoading: saveRestaurant.isPending || removeRestaurant.isPending,
    error: saveRestaurant.error || removeRestaurant.error,
  };
};